import { createContext, useContext, useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { ref, get, onValue } from "firebase/database";
import { rtdb } from "../services/firebase";

import QRCode from "qrcode";

import { sendMail } from "../services/mailService";

import { calcularFrete } from "../services/shippingService";
import { createOrderFromCheckout } from "../services/createOrderFromCheckout";

import { useNavigate } from "react-router-dom";

import { calcCreditTotal } from "../utils/calcCreditTotal";

const CheckoutContext = createContext();

const WHATSAPP_STORE_PHONE = "5531981020410";

function buildWhatsappOrderMessage({ client, address, shipping, items, subtotal, totalAmount }) {
    const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

    const lines = [];
    lines.push("Olá! Gostaria de finalizar meu pedido via link de pagamento (crédito):");
    lines.push("");
    lines.push("*Itens do pedido:*");
    items.forEach((it) => {
        const nome = it.productName || it.name || "Item";
        const sabor = it.flavor ? ` (${it.flavor})` : "";
        lines.push(`- ${nome}${sabor} x${it.quantity} — ${fmt(it.price * it.quantity)}`);
    });
    lines.push("");
    lines.push(`Subtotal: ${fmt(subtotal)}`);
    lines.push(`Frete (${shipping?.carrier || shipping?.method || "-"}): ${fmt(shipping?.price)}`);
    lines.push(`*Total: ${fmt(totalAmount)}*`);
    lines.push("");
    lines.push("*Dados do cliente:*");
    lines.push(`Nome: ${client?.name || "-"}`);
    lines.push(`Telefone: ${client?.phone || "-"}`);
    lines.push(`E-mail: ${client?.email || "-"}`);
    lines.push(`CPF: ${client?.cpf || "-"}`);
    lines.push("");
    lines.push("*Endereço de entrega:*");
    lines.push(`${address?.street || ""}, ${address?.number || ""}${address?.complement ? " - " + address.complement : ""}`);
    lines.push(`${address?.district || ""} - ${address?.city || ""}/${address?.state || ""}`);
    lines.push(`CEP: ${address?.cep || ""}`);

    return lines.join("\n");
}

export function CheckoutProvider({ children }) {

    const navigate = useNavigate();

    const submittingPaymentRef = useRef(false);
    const creatingOrderRef = useRef(false);
    const processedPaymentIdRef = useRef(null);
    const paymentStatusRef = useRef("IDLE");

    const checkoutReferenceIdRef = useRef(null);
    if (!checkoutReferenceIdRef.current) {
      checkoutReferenceIdRef.current =
        `ORDER_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    const [activeStep, setActiveStep] = useState(0);
    const { user, cart, clearUserCart } = useAuth();
    const [items, setItems] = useState([]);
    const [hasSavedAddresses, setHasSavedAddresses] = useState(false);

    const [shippingOptions, setShippingOptions] = useState([]);
    const [loadingShipping, setLoadingShipping] = useState(false);

    const [discountLimit, setDiscountLimit] = useState(0);
    const [isWholesale, setIsWholesale] = useState(false);
    const [subtotalNormal, setSubtotalNormal] = useState(0);

    // ===============================
    // PAGAMENTO / PAGBANK
    // ===============================
    const [orderId, setOrderId] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState("IDLE"); 

    // IDLE | CREATED | WAITING_PAYMENT | PAID | FAILED

    const [pixQrCode, setPixQrCode] = useState(null);
    const [pixCopyPaste, setPixCopyPaste] = useState(null);

    const [installments, setInstallments] = useState(1);

    useEffect(() => {
      paymentStatusRef.current = paymentStatus;
    }, [paymentStatus]);

    function nextStep() {
        setActiveStep((prev) => prev + 1);
    }

    function prevStep() {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    }

    const [checkoutData, setCheckoutData] = useState({
        client: {
            name: "",
            email: "",
            phone: "",
            cpf: "",
        },
        address: {
            cep: "",
            street: "",
            number: "",
            complement: "",
            district: "",
            city: "",
            state: "",
            saveAsDefault: false,
        },
        shipping: {
            method: "",
            price: 0,
            prazo: "",
            carrier: "",
        },
        payment: {
            method: "",
            installments: 1,
        },
    });

    function updateSection(section, data) {
        setCheckoutData((prev) => ({
            ...prev,
            [section]: { ...prev[section], ...data },
        }));
    }

    function resolveGatewayPaymentId(paymentResponse) {
      return (
        paymentResponse?.payment_code ||
        paymentResponse?.transactionId ||
        paymentResponse?.id ||
        null
      );
    }

    // Carregar produtos do carrinho do Firebase
    useEffect(() => {

        async function loadCartItems() {

            if (!cart || cart.length === 0) {
                setItems([]);
                return;
            }

            try {

                const discountSnap = await get(ref(rtdb, "app/config/discount"));
                const discountValue = discountSnap.exists()
                  ? Number(discountSnap.val())
                  : 0;

                setDiscountLimit(discountValue);

                const productsData = await Promise.all(
                  cart.map(async (item) => {
                    const snap = await get(ref(rtdb, `products/${item.productId}`));
                    if (!snap.exists()) return null;

                    const product = snap.val();

                    const rawPrice = product.precos.isPromo
                      ? product.precos.promo
                      : product.precos.normal;

                    const normalPrice = parseFloat(
                      (rawPrice || "0")
                        .replace(/[^\d,]/g, "")
                        .replace(",", ".")
                    );

                    const wholesalePrice = parseFloat(
                      (product.precos?.discount || "0")
                        .replace(/[^\d,]/g, "")
                        .replace(",", ".")
                    );

                    return {
                      productId: item.productId,
                      productName: product.nome,
                      flavor: item.flavor,
                      quantity: item.quantity,
                      normalPrice: isNaN(normalPrice) ? 0 : normalPrice,
                      wholesalePrice: isNaN(wholesalePrice) ? 0 : wholesalePrice,
                      image: product.imagens?.[0] || null,
                    };
                  })
                );

                const validItems = productsData.filter(Boolean);

                // subtotal SEM desconto
                const normalSubtotal = validItems.reduce(
                  (sum, item) => sum + item.normalPrice * item.quantity,
                  0
                );

                setSubtotalNormal(normalSubtotal);

                // decide se aplica atacado
                let wholesale = normalSubtotal > discountValue;

                setIsWholesale(wholesale);

                // aplica preço final no item
                setItems(
                  validItems.map((item) => ({
                    ...item,
                    price: wholesale ? item.wholesalePrice : item.normalPrice,
                  }))
                );

            } catch (err) {
                console.error("Erro ao carregar itens do carrinho:", err);
                setItems([]);
            }
        }

        loadCartItems();
    }, [cart]);

    // Preencher dados do usuário automaticamente
    useEffect(() => {
        if (user && activeStep === 0) {
            updateSection("client", {
                name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "",
                email: user.email || "",
                phone: user.phone || "",
                cpf: String(user.cpf) || "",
            });
        }
    }, [user, activeStep]);

    // Verificar se usuário tem endereços salvos e carregar endereço padrão
    useEffect(() => {
        async function loadUserAddress() {
            if (!user?.uid || activeStep !== 1) return;

            try {
                const snap = await get(ref(rtdb, `users/client/${user.uid}`));
                if (!snap.exists()) {
                    setHasSavedAddresses(false);
                    return;
                }

                const data = snap.val();
                let address = null;
                let hasAddresses = false;

                // Priorizar endereços múltiplos (novo formato)
                if (data.addresses) {
                    const addressesList = Object.keys(data.addresses).map(key => ({
                        id: key,
                        ...data.addresses[key]
                    }));
                    hasAddresses = addressesList.length > 0;
                    address = addressesList.find(addr => addr.isDefault) || addressesList[0];
                } else if (data.address || data.adress) {
                    // Compatibilidade com formato antigo (endereço único)
                    address = data.address || data.adress;
                    hasAddresses = !!address;
                }

                setHasSavedAddresses(hasAddresses);

                if (address) {
                    updateSection("address", {
                        cep: address.zipCode || address.cep || "",
                        street: address.street || "",
                        number: address.number || "",
                        complement: address.complement || "",
                        district: address.neighborhood || address.district || "",
                        city: address.city || "",
                        state: address.state || "",
                        saveAsDefault: false, // Reset ao carregar endereço existente
                    });
                }
            } catch (err) {
                console.error("Erro ao carregar endereço do usuário:", err);
                setHasSavedAddresses(false);
            }
        }

        loadUserAddress();
    }, [user, activeStep]);

    async function fetchShippingOptions() {

      if (!checkoutData.address.cep || items.length === 0) return;

      setLoadingShipping(true);

      const result = await calcularFrete({
        cepDestino: checkoutData.address.cep,
        items,
      });

      // Normaliza retorno do Melhor Envio
      const normalized = (result || [])
        .filter((opt) => opt.price)
        .map((opt) => ({
          id: opt.id,
          method: opt.name,
          carrier: opt.company?.name || opt.company?.label,
          price: Number(opt.price),
          prazo: `${opt.delivery_time} dias`,
        }));

      setShippingOptions(normalized);
      setLoadingShipping(false);

    };

    useEffect(() => {
      if (activeStep === 2) {
        fetchShippingOptions();
      }
    }, [activeStep]);

    // ===============================
    // HELPERS
    // ===============================
    const subtotal = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const creditCalc = calcCreditTotal({
      subtotal,
      shipping: checkoutData.shipping.price || 0,
      installments: checkoutData.payment.installments || 1,
    });

    const totalAmount =
      checkoutData.payment.method === "credit"
        ? creditCalc.total
        : subtotal + checkoutData.shipping.price;

    // const totalAmount =
    //   checkoutData.payment.method === "credit"
    //     ? creditCalc.total
    //     : subtotal;

    const amountInCents = Math.round(totalAmount * 100);

    // ===============================
    // API — PAGBANK
    // ===============================

    async function sendConfirmMail() {

      console.log("🚀 VOU ENVIAR EMAIL AGORA");

      try {

        await sendMail({
          mail: checkoutData.client.email,
          type: "pagamento_confirmado",
        });

        await sendMail({
          mail: "emma.suporte@gmail.com",
          type: "nova_compra",
        });

      } catch (err) {
        console.log(err);
      }
              
    }

    async function onPaymentApproved(paymentResponse, approvedAmountInCents, pType) {

      const gatewayPaymentId = resolveGatewayPaymentId(paymentResponse) || orderId;

      if (!gatewayPaymentId) {
        console.warn("Pagamento aprovado sem identificador do gateway:", paymentResponse);
        return;
      }

      if (processedPaymentIdRef.current === gatewayPaymentId) return;
      if (creatingOrderRef.current || paymentStatusRef.current === "PROCESSING") return;

      creatingOrderRef.current = true;
      processedPaymentIdRef.current = gatewayPaymentId;
      setPaymentStatus("PROCESSING");

      try {
        
        const { client, address, shipping, payment } = checkoutData;

        const result = await createOrderFromCheckout({
          user,
          items,
          client,
          address,
          shipping,
          payment: {
            referenceId: checkoutReferenceIdRef.current,
            method: pType,
            installments: payment.installments || 1,
            card: payment.card ? { ...payment.card } : null,
            gatewayOrderId: gatewayPaymentId,
          },
          amountInCents: approvedAmountInCents
        });

        if (!result?.ok) {
          throw result?.error || new Error("Falha ao criar pedido");
        }

        setPaymentStatus("PAID");
        clearUserCart();

        navigate("/meus-pedidos", {
          state: {
            orderSuccess: true,
            orderId: result.orderId,
          },
        });

      } catch (err) {
        console.error("Erro ao criar pedido:", err);
        processedPaymentIdRef.current = null;
        setPaymentStatus(pType === "pix" ? "WAITING_PAYMENT" : "FAILED");
      } finally {
        creatingOrderRef.current = false;
      }

    }

    useEffect(() => {
      if (!orderId) return;
      if (paymentStatus !== "WAITING_PAYMENT") return;

      const gatewayRef = ref(rtdb, `gatewayCreate/${orderId}`);

      const unsubscribe = onValue(gatewayRef, async (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.val();
        const gatewayStatus = String(data?.payment_status || data?.status || "").toLowerCase();
        const approved = gatewayStatus === "approved" || gatewayStatus === "paid" || Boolean(data?.approved_at);

        if (!approved) return;

        // FastPay (sandbox ou produção): pedido já criado pelo webhook, apenas navega
        const resultOrderId = data?.orderId || orderId;
        setPaymentStatus("PAID");
        clearUserCart();
        navigate("/meus-pedidos", {
          state: { orderSuccess: true, orderId: resultOrderId },
        });
      });

      return () => unsubscribe();
    }, [checkoutData.payment.method, orderId, paymentStatus, amountInCents, checkoutData, items, user]);

    function normalizeItems({ items, shippingPrice }) {
      const map = {};

      // 🔹 Consolida produtos
      items.forEach((item) => {
        const unitAmount = Math.round(item.price * 100);
        const key = `${item.productName}_${unitAmount}`;

        if (!map[key]) {
          map[key] = {
            title: item.productName,
            quantity: 0,
            unit_amount: unitAmount,
          };
        }

        map[key].quantity += item.quantity;
      });

      const normalizedItems = Object.values(map);

      // 🔹 Adiciona frete como item (se existir)
      if (shippingPrice && shippingPrice > 0) {
        normalizedItems.push({
          title: "Frete",
          quantity: 1,
          unit_amount: Math.round(shippingPrice * 100),
        });
      }

      return normalizedItems;
    }

    async function criarPagamentoMangofy(paymentType) {

      setPaymentStatus("CREATED");

      try {

        const isCredit = paymentType !== "pix";

        let rawYear;
        let expYear;

        if (isCredit) {

          rawYear = String(checkoutData.payment.card.year);

          expYear =
            rawYear.length === 2
              ? Number(`20${rawYear}`)
              : Number(rawYear);

        }

        const payload = {
          store_code: "b8e2cb5ded1ef63495ab05972caa8c71",
          external_code: checkoutReferenceIdRef.current,
          payment_method: isCredit ? "credit_card" : "pix",
          payment_format: "regular",
          installments: checkoutData.payment.installments || 1,
          payment_amount: amountInCents,
          shipping_amount: Math.round(checkoutData.shipping.price * 100),

          items: items.map(item => ({
            name: item.productName,
            quantity: item.quantity,
            unit_amount: Math.round(item.price * 100)
          })),

          customer: {
            email: checkoutData.client.email,
            name: checkoutData.client.name,
            document: checkoutData.client.cpf,
            phone: checkoutData.client.phone,
            ip: "45.87.42.74"
          },

          ...(isCredit && {
            card: {
              number: checkoutData.payment.card.number,
              holder_name: checkoutData.payment.card.name,
              expiration_month: Number(checkoutData.payment.card.month),
              expiration_year: expYear,
              cvv: checkoutData.payment.card.cvv,
              soft_descriptor: "VVTECH LTDA"
            }
          }),

          ...(!isCredit && {
            postback_url: "https://webhookmangofy-2gw7h3kv7q-uc.a.run.app/webhookMangofy",
            pix: {
              expires_in_days: 1
            },
            extra: {
              metadata: {
                userId: user?.uid || null,
                items,
                client: checkoutData.client,
                address: checkoutData.address,
                shipping: checkoutData.shipping,
              }
            }
          })
        };

        const response = await fetch(
          "https://emmapods.com/api/mangofy.php",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          }
        );

        const data = await response.json();

        if (!response.ok) throw new Error("Erro Mangofy");

        setOrderId(data.payment_code);

        if (data.payment_method === "pix") {

          setPaymentStatus("WAITING_PAYMENT");

          const qrText = data.pix.pix_qrcode_text;

          const qrImage = await QRCode.toDataURL(qrText, {
            width: 300,
            margin: 2
          });

          setPixQrCode(qrImage);
          setPixCopyPaste(qrText);

          return data.payment_code;

        } else {

          if (data.payment_status === "approved") {
            await onPaymentApproved(data, amountInCents, paymentType);
          }

        }

        return data;

      } catch (err) {
        console.log(err);
      }

    }

    async function criarOrderPagbank(paymentType) {

        setPaymentStatus("CREATED");

        let pType =
          paymentType === "credit"
            ? "CREDIT_CARD"
            : "PIX";

        const rawYear = String(checkoutData.payment.card.year);

        const expYear =
          rawYear.length === 2
            ? Number(`20${rawYear}`)
            : Number(rawYear);

        const isCredit = pType === "CREDIT_CARD";

        const pagbankItems = normalizeItems({
          items,
          shippingPrice: checkoutData.shipping.price,
        });

        const response = await fetch(`https://criarorderpagbank-2gw7h3kv7q-uc.a.run.app/criarOrderPagbank`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: amountInCents,
            paymentMethod: "credit_card",
            customer: {
              name: checkoutData.client.name,
              email: checkoutData.client.email,
              phone: checkoutData.client.phone,
              document: {
                type: "cpf",
                number: checkoutData.client.cpf
              }
            },
            card: {
              number: checkoutData.payment.card.number,
              holderName: checkoutData.payment.card.name,
              expiryMonth: String(checkoutData.payment.card.month),
              expiryYear: expYear,
              cvv: checkoutData.payment.card.cvv,
              installments: Number(checkoutData.payment.installments)
            },
            items: pagbankItems,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          setPaymentStatus("FAILED");
          throw new Error("Erro ao criar order");
        }

        if (data.charges[0].status === "PAID") {
          setPaymentStatus("PAID");
          await sendConfirmMail();
          await onPaymentApproved(data, amountInCents, "card");
        }

        setOrderId(data.id);
        return data.id;
    }

    async function criarPixPagbank() {

        setPaymentStatus("CREATED");

        const realCep = (checkoutData.address.cep.replace(/\D/g, "")).slice(0, 8);

        const response = await fetch(`https://pagarcompix-2gw7h3kv7q-uc.a.run.app/pagarComPix`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            referenceId: checkoutReferenceIdRef.current,
            customer: {
              name: checkoutData.client.name,
              email: checkoutData.client.email,
              tax_id: checkoutData.client.cpf,
            },
            items: items.map((item) => ({
              name: item.productName,
              quantity: item.quantity,
              unit_amount: Math.round(item.price * 100),
            })),
            qr_codes: [{
                amount: {
                    value: amountInCents,
                },
            }],
            metadata: {
              userId: user?.uid || null,
              items,
              client: checkoutData.client,
              address: checkoutData.address,
              shipping: checkoutData.shipping,
            },
            notification_urls: ["https://webhookpagbank-2gw7h3kv7q-uc.a.run.app/webhookPagbank"],
            shipping: checkoutData.address
              ? {
                  address: {
                    street: checkoutData.address.street,
                    number: checkoutData.address.number,
                    locality: checkoutData.address.district,
                    city: checkoutData.address.city,
                    region_code: checkoutData.address.state,
                    country: "BRA",
                    postal_code: realCep,
                  },
                }
              : undefined,
          }),
        });

        const data = await response.json();

          if (!response.ok) {
            throw new Error("Erro ao criar order PIX");
          }

          const pix = data.qr_cdoes[0];

          setPaymentStatus("WAITING_PAYMENT");
          setOrderId(data.id);
          setPixCopyPaste(pix.text);
          setPixQrCode(pix.links[0].href);

          return data.id;

    }

    // ===============================
    // FASTPAY SANDBOX
    // ===============================

    async function criarPixFastpaySandboxLocal() {
      setPaymentStatus("CREATED");

      const { criarPixFastpaySandbox } = await import("../services/fastpayService");

      const data = await criarPixFastpaySandbox({
        totalAmount,
        items,
        client: checkoutData.client,
        address: checkoutData.address,
        shipping: checkoutData.shipping,
        userId: user?.uid || null,
      });

      const chargeId = data?.id;
      if (!chargeId) throw new Error("FastPay não retornou charge ID");

      setOrderId(chargeId);
      setPaymentStatus("WAITING_PAYMENT");

      const pixPayload = data?.pix || data?.paymentDetails || data?.payment_details || {};
      const qrText =
        pixPayload?.copyPaste ||
        pixPayload?.emv ||
        pixPayload?.qrCode ||
        pixPayload?.pix_qrcode_text;

      if (qrText) {
        const qrImage = await QRCode.toDataURL(qrText, { width: 300, margin: 2 });
        setPixQrCode(qrImage);
        setPixCopyPaste(qrText);
      }

      return chargeId;
    }

    async function criarPixFastpayLocal() {
      setPaymentStatus("CREATED");

      const { criarPixFastpay } = await import("../services/fastpayService");

      const data = await criarPixFastpay({
        totalAmount,
        items,
        client: checkoutData.client,
        address: checkoutData.address,
        shipping: checkoutData.shipping,
        userId: user?.uid || null,
      });

      const chargeId = data?.id;
      if (!chargeId) throw new Error("FastPay não retornou charge ID");

      setOrderId(chargeId);
      setPaymentStatus("WAITING_PAYMENT");

      const pixPayload = data?.pix || data?.paymentDetails || data?.payment_details || {};
      const qrText =
        pixPayload?.copyPaste ||
        pixPayload?.emv ||
        pixPayload?.qrCode ||
        pixPayload?.pix_qrcode_text;

      if (qrText) {
        const qrImage = await QRCode.toDataURL(qrText, { width: 300, margin: 2 });
        setPixQrCode(qrImage);
        setPixCopyPaste(qrText);
      }

      return chargeId;
    }

    async function criarCartaoFastpayLocal() {
      setPaymentStatus("CREATED");

      const { criarCartaoFastpay } = await import("../services/fastpayService");

      const data = await criarCartaoFastpay({
        totalAmount,
        installments: checkoutData.payment.installments || 1,
        card: checkoutData.payment.card,
        client: checkoutData.client,
        address: checkoutData.address,
        shipping: checkoutData.shipping,
        items,
        userId: user?.uid || null,
      });

      const chargeId = data?.id;
      if (!chargeId) throw new Error("FastPay não retornou charge ID");

      setOrderId(chargeId);
      setPaymentStatus("WAITING_PAYMENT");

      return chargeId;
    }

    async function criarCartaoFastpaySandboxLocal() {
      setPaymentStatus("CREATED");

      const { criarCartaoFastpaySandbox } = await import("../services/fastpayService");

      const data = await criarCartaoFastpaySandbox({
        totalAmount,
        installments: checkoutData.payment.installments || 1,
        card: checkoutData.payment.card,
        client: checkoutData.client,
        address: checkoutData.address,
        shipping: checkoutData.shipping,
        items,
        userId: user?.uid || null,
      });

      const chargeId = data?.id;
      if (!chargeId) throw new Error("FastPay não retornou charge ID");

      // O pedido é criado pelo webhook no servidor.
      // Ativamos o listener do RTDB e aguardamos o sinal de confirmação.
      setOrderId(chargeId);
      setPaymentStatus("WAITING_PAYMENT");

      return chargeId;
    }

    // ===============================
    // FLUXO FINAL
    // ===============================
    async function finalizarCheckout() {

      const isFastpaySandbox = process.env.REACT_APP_PAYMENT_SANDBOX === "fastpay";

      try {

        if (checkoutData.payment.method === "credit_link") {
          const msg = buildWhatsappOrderMessage({
            client: checkoutData.client,
            address: checkoutData.address,
            shipping: checkoutData.shipping,
            items,
            subtotal,
            totalAmount,
          });
          const url = `https://wa.me/${WHATSAPP_STORE_PHONE}?text=${encodeURIComponent(msg)}`;
          window.open(url, "_blank");
          return;
        }

        if (checkoutData.payment.method !== "pix") {

          if (paymentStatus === "CREATED" || paymentStatus === "PAID") return;

          if (submittingPaymentRef.current) return;
          submittingPaymentRef.current = true;

          if (isFastpaySandbox) {
            await criarCartaoFastpaySandboxLocal();
          } else {
            await criarCartaoFastpayLocal();
          }

        } else {

            if (
              submittingPaymentRef.current ||
              paymentStatus === "WAITING_PAYMENT" ||
              paymentStatus === "PROCESSING" ||
              paymentStatus === "PAID"
            ) {
              return;
            }

            submittingPaymentRef.current = true;

            if (isFastpaySandbox) {
              const chargeId = await criarPixFastpaySandboxLocal();
              if (!chargeId) {
                toast.error("Sem chargeId gerado!");
                return;
              }
            } else {
              const chargeId = await criarPixFastpayLocal();
              if (!chargeId) {
                toast.error("Sem chargeId gerado!");
                return;
              }
            }

        }

      } catch (err) {
        console.log(err);
        setPaymentStatus("FAILED");
      } finally {
        submittingPaymentRef.current = false;
      }
    }

    return (
        <CheckoutContext.Provider
            value={{
                activeStep,
                setActiveStep,
                nextStep,
                prevStep,
                checkoutData,
                updateSection,
                items,
                hasSavedAddresses,
                user,
                shippingOptions,
                loadingShipping,
                installments,
                setInstallments,
                orderId,
                paymentStatus,
                pixQrCode,
                pixCopyPaste,
                finalizarCheckout,
                subtotal,
                totalAmount,
                creditCalc,
                isWholesale,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
}

export function useCheckout() {
    return useContext(CheckoutContext);
}
